import axios from 'axios';

export class ConfigService {
  private static instance: ConfigService;
  private config: any = {};

  static getInstance(): ConfigService {
    if (!ConfigService.instance) {
      ConfigService.instance = new ConfigService();
    }
    return ConfigService.instance;
  }

  getApiUrl(): string {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    
    console.log(`🌐 Current hostname: ${hostname}`);
    
    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:8080/api';
    }
    
    // Staging environments
    if (hostname === 'dev.kairosiq.ai' || hostname === 'dev.agentkairos.com') {
      return `${protocol}//${hostname}/api`;
    }
    
    // Production environments
    if (hostname === 'app.kairosiq.ai' || hostname === 'app.agentkairos.com') {
      return `${protocol}//${hostname}/api`;
    }
    
    // Default to relative URL
    return '/api';
  }

  getAiUrl(): string {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    
    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:8080/ai';
    }
    
    // All other environments
    return `${protocol}//${hostname}/ai`;
  }

  getAppUrl(): string {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    
    return `${protocol}//${hostname}`;
  }

  getDomain(): string {
    const hostname = window.location.hostname;
    
    // Extract base domain
    if (hostname.includes('kairosiq.ai')) {
      return 'kairosiq.ai';
    } else if (hostname.includes('agentkairos.com')) {
      return 'agentkairos.com';
    }
    
    return hostname;
  }

  getGoogleRedirectUri(): string {
    return `${this.getAppUrl()}/google/callback`;
  }

  isStaging(): boolean {
    const hostname = window.location.hostname;
    return hostname.startsWith('dev.');
  }

  isProduction(): boolean {
    const hostname = window.location.hostname;
    return hostname === 'app.kairosiq.ai' || hostname === 'app.agentkairos.com';
  }

  async getStripeConfig(): Promise<{ publishableKey: string }> {
    try {
      // Fetch Stripe config from backend
      const response = await fetch(`${this.getApiUrl()}/config/stripe`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch Stripe config: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.data?.publishableKey) {
        console.log('✅ [Stripe] Config loaded from server');
        return {
          publishableKey: data.data.publishableKey
        };
      } else {
        throw new Error('Invalid Stripe config response');
      }
    } catch (error) {
      console.error('❌ [Stripe] Failed to fetch config:', error);
      
      // Fallback to environment variable if available
      const fallbackKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
      if (fallbackKey && fallbackKey !== 'pk_test_dummy') {
        console.warn('⚠️ [Stripe] Using fallback publishable key from environment');
        return {
          publishableKey: fallbackKey
        };
      }
      
      throw new Error('Stripe configuration unavailable');
    }
  }
}

const configService = ConfigService.getInstance();

// Create axios instance with dynamic base URL
const axiosInstance = axios.create({
  baseURL: configService.getApiUrl(),
  withCredentials: true,
});

// Add request interceptor for debugging and authentication
axiosInstance.interceptors.request.use(
  (config) => {
    console.log(`📤 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    
    // Add JWT token to Authorization header if available
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('🔐 Added Bearer token to request');
    }
    
    return config;
  },
  (error) => {
    console.error('❌ Request error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    console.log(`📥 API Response: ${response.status} ${response.config.url}`);
    
    // Store token if it's a login response
    if (response.config.url?.includes('/auth/login') && response.data?.token) {
      localStorage.setItem('auth_token', response.data.token);
      localStorage.setItem('token', response.data.token); // For backward compatibility
      console.log('🔐 Token stored successfully');
    }
    
    return response;
  },
  (error) => {
    if (error.response) {
      console.error(`❌ API Error: ${error.response.status} ${error.config?.url}`);
      
      // Handle 401 Unauthorized
      if (error.response.status === 401) {
        console.warn('🔓 Unauthorized - clearing token');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('token');
      }
    } else if (error.request) {
      console.error('❌ No response received:', error.config?.url);
    } else {
      console.error('❌ Request setup error:', error.message);
    }
    return Promise.reject(error);
  }
);

export default configService;
export { axiosInstance };
